import styled from "styled-components";
import { DesignTokens } from "melco-ui";
import { isOrderError } from "./statusResolvers";
import { SELECTED_BG, SELECTED_ACCENT } from "./summitTheme";

const { spacings, colors } = DesignTokens;

type DisplaySummitDetailsOrderStatusProps = {
    filteredOrders: any[];
    onClick?: (selectedOrder: any, deviceId: string) => void;
    selectedOrder: any;
};

const OrderRow = styled.div<{ $selected?: boolean }>`
    display: flex;
    align-items: flex-start;
    
    padding: ${spacings.small}px  ${spacings.small}px ${spacings.small}px ${spacings.small}px;
    border-bottom: 1px solid ${colors.gray.middle};
    border-left: 3px solid transparent;
    cursor: pointer;
    transition: background-color 0.1s ease, border-color 0.1s ease;

    &:hover {
        background-color: ${colors.gray.light};
    }

    /* Same "selected" treatment as UserResultCard — a visible red tint
       plus a red accent bar — so the highlighted order is obvious
       instead of blending into the un-selected rows around it. */
    ${({ $selected }) =>
        $selected &&
        `
            background-color: ${SELECTED_BG};
            border-left-color: ${SELECTED_ACCENT};
        `}
`;

// WHAT: the row's inner two-column layout (icon | name/id/last-scan).
// WHY a dedicated flex wrapper rather than putting these two columns
// directly inside OrderRow: OrderRow itself is already a flex row
// with the status dot pinned to its far right (justify-content:
// space-between) — nesting the icon+text pairing in its own flex
// container keeps "icon column sized to the icon, text column takes
// the rest" independent of that outer row/dot layout.
const OrderMain = styled.div`
    display: flex;
    align-items: flex-start;
    gap: ${spacings.xsmall}px;
    min-width: 0;
`;

// WHAT: the icon's column. No explicit width is set on purpose — a flex
// item with no flex-grow sizes itself to its content by default, so this
// column is exactly as wide as the icon (20px) and never wider, per the
// "left column width only up to icon width" requirement.
const OrderIconColumn = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
`;

// Simple monitor glyph standing in for "this is a order" — deliberately
// generic/schematic rather than a literal product photo, matching the
// plain-line-icon style already used for StatusDot elsewhere in this file.
// const OrderIcon: React.FC = () => (
//     <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
//         <rect x="3" y="4" width="18" height="12" rx="1.5" stroke={colors.gray.middle} strokeWidth="1.5" />
//         <path d="M9 20h6M12 16v4" stroke={colors.gray.middle} strokeWidth="1.5" strokeLinecap="round" />
//     </svg>
// );
const OrderIcon: React.FC = () => <img src={""} width={0} height={0} alt="" />;

const OrderTextColumn = styled.div`
    /* Takes whatever space is left after the icon column — this is what
       makes the text start flush at the left edge of its own column
       rather than being centered/pushed by the icon's width. */
    flex: 1;
    min-width: 0;
`;

const OrderNameText = styled.div<{ $selected?: boolean }>`
    font-size: 14px;
    font-weight: 500;
    color: ${({ $selected }) => ($selected ? SELECTED_ACCENT : colors.gray.dark)};
`;

const OrderIdText = styled.div`
    font-size: 11px;
    color: ${colors.gray.dark};
    margin-top: 1px;
`;

const LastScanText = styled.div`
    font-size: 11px;
    color: ${colors.gray.middle};
    margin-top: 2px;
`;


export const DisplaySummitDetailsOrderStatus: React.FC<
    DisplaySummitDetailsOrderStatusProps
> = ({ selectedOrder, filteredOrders, onClick }) => {
    return (
        <>
            {filteredOrders?.map((order: any) => {
                const isSelected = selectedOrder?.id === order.id;
                // const isError = isOrderError(order);
                const displayName = order?.design_name || order?.id;

                return (
                    <OrderRow
                        key={order.id}
                        $selected={isSelected}
                        onClick={() => onClick?.(order, order.id)}
                    >
                        <OrderMain>
                            <OrderIconColumn>
                                <OrderIcon />
                            </OrderIconColumn>
                            <OrderTextColumn>
                                <OrderNameText $selected={isSelected}>{displayName}</OrderNameText>
                                <OrderIdText>Id: {order.id}</OrderIdText>
                                <OrderIdText>Internal id: {order.internal_order_id}</OrderIdText>
                                <OrderIdText>Status: {order?.order_status ?? ""}</OrderIdText>
                                {/*  an ISO date string from the orders-list API.
                                    Guarded with a null-check + try/catch-free Date parse: if the
                                    value is missing or unparseable we just omit the line instead
                                    of rendering "Invalid Date" to the rep. */}
                                {order.order_status && (
                                    <LastScanText>
                                        Last updated: {new Date(order?.updated_date).toLocaleString()}
                                    </LastScanText>
                                )}
                                {/*  */}
                                {/* {order._owner === "manager" && <OwnerTag>Manager's order</OwnerTag>} */}
                            </OrderTextColumn>
                        </OrderMain>

                    </OrderRow>
                );
            })}
        </>
    );
};
